import { Divider, Stack, Text } from '@chakra-ui/react';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineSquircleIcon from 'components/_core/SunshineSquircleIcon';
import Loader from 'components/Loader';
import { formatAddress } from 'helpers/address';
import { formatDate, PPP } from 'helpers/date';
import { formatSIRET } from 'helpers/siret';

import CloseAccountLink from '../CloseAccountLink';
import Section from '../Section';
import locales from './locales';

const GeneralInformation = () => {
  const { companyProfileId } = useCompanyContext();
  const { company, loading: companyLoading } =
    useCompanyProfile(companyProfileId);

  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  if (companyLoading || !company) {
    return <Loader />;
  }

  return (
    <>
      <Section title={locales.details}>
        <Stack divider={<Divider />} spacing="space-16">
          {company.legalName ? (
            <Form.Field label={locales.rows.legalName}>
              <Text fontWeight="weight-500" variant="primary">
                {company.legalName}
              </Text>
            </Form.Field>
          ) : null}

          {company.tradeName ? (
            <Form.Field
              hint={locales.descriptions.tradeName}
              label={locales.rows.tradeName}
            >
              <Text fontWeight="weight-500" variant="primary">
                {company.tradeName}
              </Text>
            </Form.Field>
          ) : null}

          {company.legalRegistrationNumber ? (
            <Form.Field
              hint={locales.descriptions.siret}
              label={locales.rows.siret}
            >
              <Text fontWeight="weight-500" variant="primary">
                {formatSIRET(company.legalRegistrationNumber)}
              </Text>
            </Form.Field>
          ) : null}

          {company.activityType ? (
            <Form.Field label={locales.rows.activityType}>
              <Text fontWeight="weight-500" variant="primary">
                {company.activityType}
              </Text>
            </Form.Field>
          ) : null}

          {company.registrationDate ? (
            <Form.Field label={locales.rows.registrationDate}>
              <Text fontWeight="weight-500" variant="primary">
                {formatDate(new Date(company.registrationDate), PPP)}
              </Text>
            </Form.Field>
          ) : null}
        </Stack>
      </Section>

      <Section title={locales.companyAddress}>
        <SunshineCard alignItems="center" title={formatAddress(company)}>
          <SunshineCard.Slot name="aside">
            <SunshineSquircleIcon name="home" />
          </SunshineCard.Slot>
        </SunshineCard>
      </Section>

      {canAccessFeature({ name: FeatureName.CompanyClose }) ? (
        <CloseAccountLink />
      ) : null}
    </>
  );
};

export default GeneralInformation;
